import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Iconify from 'src/components/iconify';

// ----------------------------------------------------------------------

export default function FormCalculation({ name, label, formula, helperText, allFields = [], ...other }) {
    const { setValue, control } = useFormContext();
    const [calculatedValue, setCalculatedValue] = useState('');
    const [calcError, setCalcError] = useState(null);

    // Watch all form values since we don't know ahead of time which fields are in the formula
    const allValues = useWatch({ control });

    useEffect(() => {
        if (!formula) {
            setCalculatedValue('');
            return;
        }

        try {
            let expression = formula;

            // 1. Define Helper Functions (Internal to evaluation context)
            const AGE = (birthDateStr) => {
                if (!birthDateStr) return 0;
                const birthDate = new Date(birthDateStr);
                if (Number.isNaN(birthDate.getTime())) return 0;
                
                const today = new Date();
                let age = today.getFullYear() - birthDate.getFullYear();
                const m = today.getMonth() - birthDate.getMonth();
                if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
                    age -= 1;
                }
                return age;
            };

            const DATEDIFF = (d1Str, d2Str) => {
                const date1 = new Date(d1Str);
                const date2 = new Date(d2Str);
                if (Number.isNaN(date1.getTime()) || Number.isNaN(date2.getTime())) return 0;
                
                const diffTime = Math.abs(date2 - date1);
                return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            };

            // 2. Identify variables {{fieldName}} or {{Label}}
            const fieldMatches = expression.match(/\{\{([^}]+)\}\}/g) || [];

            fieldMatches.forEach(match => {
                const identifier = match.replace(/\{\{|\}\}/g, '').trim();
                
                // Find matching field by Name or Label
                const field = allFields.find(f => f.name === identifier || f.label === identifier);
                
                if (field) {
                    const rawValue = allValues[field.name];

                    // If it's used inside a function like AGE(), we pass it as a string
                    // Otherwise we pass it as a number
                    let resolvedValue;
                    if (typeof rawValue === 'string' && (rawValue.includes('-') || rawValue.includes('/'))) {
                        // Likely a date string
                        resolvedValue = `"${rawValue}"`;
                    } else if (typeof rawValue === 'number') {
                        resolvedValue = rawValue;
                    } else {
                        resolvedValue = Number(rawValue) || 0;
                    }

                    expression = expression.split(match).join(String(resolvedValue));
                } else {
                    // If no field found, default to 0 to prevent crash but it's likely a user error
                    expression = expression.split(match).join('0');
                }
            });

            // 3. Sanitization
            const sanitizedExpression = expression.replace(/[^0-9.+\-*/()\s,a-zA-Z_]/g, '');

            // 4. Safely evaluate
            if (sanitizedExpression.trim() !== '') {
                // eslint-disable-next-line no-new-func
                const contextFn = new Function('AGE', 'DATEDIFF', `return ${sanitizedExpression}`);
                const result = contextFn(AGE, DATEDIFF);

                if (result === undefined || result === null || Number.isNaN(result)) {
                    setCalculatedValue('');
                    setValue(name, '', { shouldDirty: true });
                } else {
                    const formattedResult = Number.isInteger(result) ? result : Number(result).toFixed(2);
                    setCalculatedValue(formattedResult);
                    setCalcError(null);
                    setValue(name, formattedResult, { shouldDirty: true, shouldValidate: true });
                }
            }

        } catch (error) {
            console.error(`Calculation error for field "${name}":`, error);
            setCalcError('Check Formula');
            setCalculatedValue('');
        }
    }, [formula, allValues, name, setValue, allFields]);

    return (
        <TextField
            fullWidth
            disabled
            name={name}
            label={label}
            value={calculatedValue}
            helperText={calcError || helperText}
            error={!!calcError}
            InputProps={{
                startAdornment: (
                    <InputAdornment position="start">
                        <Iconify icon="solar:math-bold" sx={{ color: 'text.disabled' }} />
                    </InputAdornment>
                ),
                ...other.InputProps,
            }}
            {...other}
        />
    );
}

FormCalculation.propTypes = {
    name: PropTypes.string.isRequired,
    label: PropTypes.string,
    formula: PropTypes.string,
    helperText: PropTypes.string,
    allFields: PropTypes.array,
};
